/**
 * @packageDocumentation
 * @module f1/fn
 */

type VoidFn = (...args: unknown[]) => void;

export interface Debounce {
  /**
   * Prohibit a method from being called more than once during the time limit
   *
   * @public
   * @function f1.debounce
   * @param delay the time to wait before executing the function, in milliseconds
   * @param fn the function to execute
   * @example
   *
   * ```ts
   * //
   * const fn = f1.debounce(1000, (a) => a + 1)
   *
   * // the event handler can be called 20 times but will execute every second
   * eventHandler(event) {
   *   fn(1);
   * }
   * ```
   */
  <F extends Function>(delay: number, func: F): F;
  /**
   *
   * @public
   * @function f1.debounce
   */
  <F extends VoidFn>(delay: number): (func: F) => F;
}

/**
 * @see Debounce
 */
export default <F extends Function>(delay: number, fn: F): F => {
  let timeout: number;

  return (function executedFunction(...args: unknown[]) {
    const later = () => {
      clearTimeout(timeout);
      fn(...args);
    };

    clearTimeout(timeout);
    timeout = (setTimeout(later, delay) as unknown) as number;
  } as unknown) as F;
};
